import React, { useState, useEffect, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import Papa from "papaparse";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { createPricelist, deletePricelist } from "../../graphql/mutations";
import { listPricelists } from "../../graphql/queries";
import {
  Input,
  IconButton,
  Card,
  Box,
  Typography,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Paper,
  TablePagination,
  Alert,
  Container,
  Grid,
  Divider,
  LinearProgress,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PricelistStep2 from "./PricelistStep2";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../App";

const CSVUpload = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [step, setStep] = useState(1);

  const navigate = useNavigate();

  const { user, userRecord } = useContext(AppContext);
  // console.log("User in:", userRecord);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);

    // Parse CSV and log part numbers without spaces when file is selected
    if (file) {
      Papa.parse(file, {
        header: false,
        complete: (results) => {
          const partNumbersWithoutSpace = [];

          results.data.slice(1).forEach((row, index) => {
            const partNumber = row[0];
            if (partNumber && !partNumber.includes(" ")) {
              partNumbersWithoutSpace.push({
                rowIndex: index + 2, // +2 accounts for header and 1-indexed CSV
                partNumber,
              });
            }
          });

          console.log("Part numbers without spaces:", partNumbersWithoutSpace);
        },
      });
    }
  };

  const parseCSV = () => {
    let validRows = 0;
    let droppedRows = 0;
    let droppedRowIndices = [];

    Papa.parse(csvFile, {
      header: false,
      complete: (results) => {
        const mappedData = results.data.slice(1).map((row) => ({
          "PART NUMBER": row[0],
          PRICE: row[1] === "Do Not Buy" ? 0 : parseFloat(row[1]),
          IMAGE1: row[2],
          IMAGE2: row[3],
        }));

        mappedData.forEach((row, index) => {
          if (isNaN(row.PRICE)) {
            droppedRows += 1;
            droppedRowIndices.push(index + 2); // +2 accounts for header and 1-indexed CSV
          } else {
            validRows += 1;
          }
        });

        setCsvData(mappedData);
        setAlert({
          validRows,
          droppedRows,
          droppedRowIndices,
        });
        setLoadingText(`Uploading ${validRows} parts`);
      },
    });
  };

  useEffect(() => {
    const fetchUploadedFiles = async () => {
      if (!userRecord || !userRecord.id) {
        console.log("User record not available yet");
        return;
      }

      console.log("Fetching uploaded files for user ID:", userRecord.id);

      try {
        // Fetch pricelists where user is the owner
        const ownedResponse = await API.graphql(
          graphqlOperation(listPricelists, {
            limit: 1000,
            filter: {
              userId: { eq: userRecord.id },
            },
          }),
        );

        const ownedPricelists = ownedResponse.data.listPricelists.items || [];

        // Fetch all pricelists and filter client-side for providerId
        // Note: providerId is not available as a filter field in GraphQL schema
        const allResponse = await API.graphql(
          graphqlOperation(listPricelists, {
            limit: 1000,
          }),
        );

        const fetchedPricelists = allResponse.data.listPricelists.items || [];

        // Filter for pricelists where user is the provider (client-side filter)
        const providedPricelists = fetchedPricelists.filter(
          (p) => p && p.providerId === userRecord.id,
        );

        // Filter out any null/undefined pricelists and combine
        const allPricelists = [
          ...ownedPricelists,
          ...providedPricelists,
        ].filter((p) => p && p.id);

        // Deduplicate by ID using Map (keeps first occurrence)
        const uniquePricelistsMap = new Map();
        allPricelists.forEach((pricelist) => {
          if (!uniquePricelistsMap.has(pricelist.id)) {
            uniquePricelistsMap.set(pricelist.id, pricelist);
          }
        });

        const uniquePricelists = Array.from(uniquePricelistsMap.values());

        // Sort by createdAt descending
        uniquePricelists.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        console.log("Uploaded files:", uniquePricelists);

        setUploadedFiles(uniquePricelists);
      } catch (error) {
        console.error("Error fetching uploaded files:", error);
      }
    };

    fetchUploadedFiles();
  }, [userRecord]);

  const handleUpload = async (file) => {
    // Validate user state at start
    if (!userRecord || !userRecord.id) {
      setShowFeedback(true);
      setFeedbackMessage("User not ready. Please wait and try again.");
      return;
    }

    let s3Key = null;
    let createdPricelistId = null;
    const uploadTimeout = 300000; // 5 minutes timeout

    try {
      setLoading(true);
      setUploadProgress(0);
      setLoadingText("Preparing upload...");

      s3Key = `csv-data/${uuidv4()}.json`;

      // Convert CSV data to JSON without any price adjustments
      const csvToUpload = (csvData || []).map((row, index) => {
        const price = parseFloat(row.PRICE);
        const isPriceValid = !Number.isNaN(price);
        return {
          ...row,
          // Store original price from CSV without adjustments
          price: isPriceValid ? price : 0,
        };
      });

      // Step 1: Upload to S3 with timeout and progress
      setLoadingText("Uploading file to storage...");
      setUploadProgress(10);

      const uploadPromise = Storage.put(s3Key, JSON.stringify(csvToUpload), {
        contentType: "application/json",
        progressCallback: (progress) => {
          const percentage =
            Math.round((progress.loaded / progress.total) * 40) + 10; // 10-50%
          setUploadProgress(percentage);
        },
      });

      // Add timeout wrapper
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Upload timeout after 5 minutes")),
          uploadTimeout,
        ),
      );

      await Promise.race([uploadPromise, timeoutPromise]);

      setUploadProgress(50);
      setLoadingText("Creating pricelist record...");

      // Re-validate user state before database operations
      if (!userRecord || !userRecord.id) {
        throw new Error("User session invalid. Please refresh and try again.");
      }

      // Step 2: Create Pricelist record without adjustments
      const pricelist = {
        validRows: alert?.validRows ?? csvToUpload.length,
        s3Key,
        userId: userRecord.id,
        // No adjustments - prices are stored as-is from CSV
        adjustments: JSON.stringify({
          percentageAdjustment: 0,
          dollarAdjustment: 0,
        }),
        filename: file.filename,
        name: file.name,
        notes: file.notes,
      };

      const createPricelistResponse = await API.graphql(
        graphqlOperation(createPricelist, { input: pricelist }),
      );

      createdPricelistId = createPricelistResponse.data.createPricelist.id;
      setUploadProgress(75);
      setLoadingText("Activating pricelist...");

      // Step 3: Update User's active pricelist
      const updateUser = /* GraphQL */ `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            activePricelistID
          }
        }
      `;

      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: userRecord.id,
            activePricelistID: createdPricelistId,
          },
        }),
      );

      setUploadProgress(100);
      setLoadingText("Upload complete!");

      // Success cleanup
      setAlert(null);
      setCsvFile(null);
      setCsvData([]);
      setLoadingText("");
      setUploadProgress(0);
      setShowFeedback(true);
      setFeedbackMessage("Pricelist uploaded and set as active successfully!");

      // Navigate after short delay to show success
      setTimeout(() => {
        navigate("/manage-pricelists");
      }, 1000);
    } catch (error) {
      console.error("Error uploading pricelist:", error);

      // Rollback logic for partial failures
      try {
        setLoadingText("Cleaning up after error...");

        // If we created a pricelist but failed later, try to delete it
        if (createdPricelistId && s3Key) {
          console.log(
            "Attempting to clean up created pricelist:",
            createdPricelistId,
          );
          await API.graphql(
            graphqlOperation(deletePricelist, {
              input: { id: createdPricelistId },
            }),
          );
        }

        // If we uploaded to S3 but failed later, try to delete the file
        if (s3Key && !createdPricelistId) {
          console.log("Attempting to clean up S3 file:", s3Key);
          await Storage.remove(s3Key);
        }
      } catch (cleanupError) {
        console.error("Error during cleanup:", cleanupError);
        // Don't throw cleanup errors, just log them
      }

      // Determine error message based on error type
      let errorMessage = "Error uploading pricelist. Please try again.";
      if (error.message.includes("timeout")) {
        errorMessage =
          "Upload timed out. Please check your connection and try again.";
      } else if (error.message.includes("User session")) {
        errorMessage = error.message;
      } else if (
        error.message.includes("NetworkError") ||
        error.message.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setShowFeedback(true);
      setFeedbackMessage(errorMessage);

      // Reset progress indicators
      setUploadProgress(0);
      setLoadingText("Loading");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.graphql(graphqlOperation(deletePricelist, { input: { id } }));
      setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
      setShowFeedback(true);
      setFeedbackMessage("Pricelist deleted successfully!");
    } catch (error) {
      console.error("Error deleting pricelist:", error);
      setShowFeedback(true);
      setFeedbackMessage("Error deleting pricelist.");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const theme = useTheme();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Card elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            {loadingText}
          </Typography>
          <Box sx={{ width: "100%", mt: 2, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {uploadProgress}% Complete
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Please do not close this window or navigate away during upload.
          </Typography>
        </Card>
      </Container>
    );
  }

  if (step === 2) {
    return (
      <PricelistStep2
        handleUpload={handleUpload}
        csvFile={csvFile}
        alert={alert}
        setAlert={setAlert}
        parseCSV={parseCSV}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Card elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
              Upload Latest Pricelist
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                sx={{ display: "none" }}
                id="csv-file-input"
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  sx={{ mb: 2 }}
                >
                  Select CSV File
                </Button>
              </label>
              {csvFile && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Selected file: {csvFile.name}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={parseCSV}
                disabled={!csvFile}
              >
                Parse CSV
              </Button>
            </Box>
          </Card>
        </Grid>

        {alert && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                mt: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Typography gutterBottom>
                Parsing completed. {alert.validRows} row(s) will be uploaded.{" "}
                {alert.droppedRows} row(s) will be dropped.
              </Typography>
              <Typography variant="body2" gutterBottom>
                Dropped row numbers: {alert.droppedRowIndices.join(", ")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                <Button variant="contained" onClick={() => setStep(2)}>
                  Continue with Upload
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAlert(null);
                    setCsvFile(null);
                  }}
                >
                  Cancel Upload
                </Button>
              </Box>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card elevation={3} sx={{ mt: 4 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Parts</TableCell>
                    <TableCell>Offsets</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadedFiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((file) => {
                      const parsedAdjustments = JSON.parse(file.adjustments);
                      const percentageAdjustment =
                        parsedAdjustments.percentageAdjustment;
                      const dollarAdjustment =
                        parsedAdjustments.dollarAdjustment;
                      return (
                        <TableRow key={file.id}>
                          <TableCell>{file.name}</TableCell>
                          <TableCell>
                            {new Date(file.createdAt).toLocaleString()}
                          </TableCell>
                          <TableCell>{file.validRows}</TableCell>
                          <TableCell>
                            {percentageAdjustment
                              ? `${percentageAdjustment}%`
                              : ""}
                            {percentageAdjustment && dollarAdjustment
                              ? " and "
                              : ""}
                            {dollarAdjustment ? `$${dollarAdjustment}` : ""}
                            {!percentageAdjustment && !dollarAdjustment
                              ? "None"
                              : ""}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(file.id)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={uploadedFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={showFeedback}
        autoHideDuration={3000}
        onClose={() => setShowFeedback(false)}
        message={feedbackMessage}
      />
    </Container>
  );
};

export default CSVUpload;
